import { CA, type CountryData, MX, US } from 'country-region-data';

import { isOperatingCountry, normaliseLocale } from '../contract/defaults';
import {
  type OperatingCountry,
  REGION_FIELD_LABEL_BY_COUNTRY,
  REGIONAL_DEFAULTS,
  type RegionFieldLabel,
} from '../contract/regional';
import {
  getRegionCodesForCountryCode,
  getRegionsForCountryCode,
} from './address-input-global';

const COUNTRIES: Readonly<Record<OperatingCountry, CountryData>> = {
  US,
  CA,
  MX,
};

export type AddressCountryOption = {
  readonly code: OperatingCountry;
  readonly name: string;
};

/** Minimum trimmed length for shared address forms; not a per-country format check. */
export const ADDRESS_POSTAL_CODE_MIN_LENGTH = 3;

/** Max trimmed length (US ZIP+4, Canadian codes, and longer international values). */
export const ADDRESS_POSTAL_CODE_MAX_LENGTH = 12;

const POSTAL_CODE_ALLOWED_CHAR_PATTERN = /^[A-Za-z0-9\s-]+$/;

/**
 * Whether a postal value is acceptable on shared {@link AddressFields} forms before save.
 * Length and charset only; market-specific formats are validated by the API.
 */
export const isAddressPostalCodeValid = (value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed.length < ADDRESS_POSTAL_CODE_MIN_LENGTH) {
    return false;
  }
  if (trimmed.length > ADDRESS_POSTAL_CODE_MAX_LENGTH) {
    return false;
  }
  return POSTAL_CODE_ALLOWED_CHAR_PATTERN.test(trimmed);
};

/**
 * Localized country name for an ISO 3166-1 alpha-2 code.
 * `Intl.DisplayNames` supplies the name for the active locale (or `REGIONAL_DEFAULTS.locale` when invalid).
 * @param code - ISO 3166-1 alpha-2 country code
 * @param locale - BCP-47 locale tag
 * @returns Localized country name, or the code when no name is available
 */
export const countryName = (code: string, locale: string): string => {
  const resolvedLocale = normaliseLocale(locale) ?? REGIONAL_DEFAULTS.locale;
  try {
    return (
      new Intl.DisplayNames([resolvedLocale], { type: 'region' }).of(code) ??
      code
    );
  } catch {
    return code;
  }
};

/**
 * Country options for an allowlist, with names in the active locale.
 * @param allowlist - Operating countries the form may offer
 * @param locale - BCP-47 locale tag
 * @returns One option per allowlist entry
 */
export const addressCountries = (
  allowlist: readonly OperatingCountry[] = [REGIONAL_DEFAULTS.country],
  locale: string = REGIONAL_DEFAULTS.locale
): readonly AddressCountryOption[] =>
  allowlist.map((code) => ({
    code,
    name: countryName(code, locale),
  }));

const regionsForCountry = (country: string) => {
  if (isOperatingCountry(country)) {
    return COUNTRIES[country][2];
  }
  return getRegionsForCountryCode(country);
};

/**
 * Short region codes for a country known to `country-region-data`.
 * @param country - ISO 3166-1 alpha-2 country code
 * @returns Region short codes, such as `CA` or `JAL`
 */
export const regionCodes = (country: string): readonly string[] => {
  if (isOperatingCountry(country)) {
    const [, , regions] = COUNTRIES[country];
    return regions.map(([, shortCode]) => shortCode);
  }
  return getRegionCodesForCountryCode(country);
};

/**
 * Official region names for a country, in the same order as {@link regionCodes}.
 * @param country - ISO 3166-1 alpha-2 country code
 * @returns Region names, such as `Ontario` or `Nuevo León`
 */
export const regionNames = (country: string): readonly string[] =>
  regionsForCountry(country).map(([name]) => name);

/**
 * True when `code` is a region short code for that country.
 * @param country - ISO 3166-1 alpha-2 country code
 * @param code - Region short code
 * @returns Whether the code is in that country's region list
 */
export const isRegionCode = (country: string, code: string): boolean =>
  regionCodes(country).includes(code);

const foldRegionKey = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

/**
 * Region short code for a region code or name, ignoring case, accents and spacing.
 * Names match the official names in `country-region-data`, so `nuevo leon` resolves but a
 * shortened name such as `Coahuila` (officially `Coahuila de Zaragoza`) does not.
 * @param country - ISO 3166-1 alpha-2 country code
 * @param value - Region code or name, such as `jal`, `Jalisco` or `Nuevo León`
 * @returns The region short code, or null when the value names no region of that country
 */
export const regionCodeFor = (
  country: string,
  value: string
): string | null => {
  const key = foldRegionKey(value);
  if (!key) {
    return null;
  }
  const regions = regionsForCountry(country);
  if (regions.length === 0) {
    return null;
  }
  const match = regions.find(
    ([name, shortCode]) =>
      foldRegionKey(shortCode) === key || foldRegionKey(name) === key
  );
  return match ? match[1] : null;
};

/**
 * Subdivision label for a country from `REGIONAL_CONFIG`.
 * Defaults to the default market's subdivision label (`state`) when unrecognized.
 * @param country - ISO 3166-1 alpha-2 country code
 * @returns `province` or `state`
 */
export const regionFieldLabel = (country: string): RegionFieldLabel =>
  isOperatingCountry(country)
    ? REGION_FIELD_LABEL_BY_COUNTRY[country]
    : REGION_FIELD_LABEL_BY_COUNTRY[REGIONAL_DEFAULTS.country];
